import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createCustomer } from '#/api/crmMgmt/customer/create';
import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import { updateCustomer } from '#/api/crmMgmt/customer/update';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import CustomerAddressesEditor from '#/modules/crmMgmt/customer/components/CustomerAddressesEditor';
import CustomerContactsEditor from '#/modules/crmMgmt/customer/components/CustomerContactsEditor';
import {
  getCustomerApiErrorMessage,
  isCustomerVersionConflict,
} from '#/modules/crmMgmt/customer/helper/apiError';
import {
  buildCustomerWritePayload,
  mapCustomerDetailToForm,
} from '#/modules/crmMgmt/customer/helper/buildCustomerWritePayload';
import { formatCustomerStatusLabel } from '#/modules/crmMgmt/customer/helper/customerStatus';
import {
  CUSTOMER_FIELD_LABELS,
  resolveCustomerFieldLabel,
} from '#/modules/crmMgmt/customer/helper/formValidationLabels';
import {
  CUSTOMER_FORM_DEFAULT_VALUES,
  customerFormSchema,
} from '#/modules/crmMgmt/customer/schemas/customerSchema';
import {
  customerDetailQueryKey,
  customerListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditCustomerPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: customerDetailQueryKey(id),
    queryFn: () => fetchCustomerDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createCustomer,
    updateFn: updateCustomer,
    buildPayload: (values) => buildCustomerWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: customerListQueryKeyPrefix,
    detailQueryKey: customerDetailQueryKey,
    getEditPath: ROUTES.customerEdit,
    getErrorMessage: (error) => getCustomerApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isCustomerVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(customerFormSchema),
    defaultValues: CUSTOMER_FORM_DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapCustomerDetailToForm(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯客戶' : '新增客戶';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const detailStatus = detailQuery.data?.data?.status;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header justify-between">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0!">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.customer}>返回列表</Link>
              </Button>
            </div>
            {isEdit ? (
              <p className="text-sm text-muted-foreground">
                狀態：{formatCustomerStatusLabel(detailStatus)}
              </p>
            ) : null}
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getCustomerApiErrorMessage(detailQuery.error, '找不到客戶資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={CUSTOMER_FIELD_LABELS}
                fieldLabelResolver={resolveCustomerFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <FloatingInput
                        id="code"
                        label="代號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="chineseName"
                        label="中文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="englishName"
                        label="英文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="taxIdNumber"
                        label="統編"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="invoiceTitle"
                        label="發票抬頭"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="snCode2"
                        label="兩碼 SN 前綴"
                        tip="選填。兩碼英文字，供 物料序號組成產品代碼，不是客戶代號。"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="snCode3"
                        label="三碼 SN 前綴"
                        tip="選填。三碼英文字，供 物料序號組成產品代碼，不是客戶代號。"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="taxCountryCode"
                        label="稅籍國碼"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="homepage"
                        label="網址"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="phone"
                        label="電話"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="fax"
                        label="傳真"
                        register={register}
                        errors={errors}
                      />
                    </div>
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                    />
                  </section>

                  <CustomerAddressesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                  />

                  <CustomerContactsEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.customer}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditCustomerPage;
